import {
  createPendingRefetchResolver,
  LIST_GC_TIME_MS,
  LIST_STALE_TIME_MS,
  useApiClient,
} from '@klareospace/pharos-lib-front-utils';
import type { ImportHistoryItem, PaginatedResponse } from '@klareospace/pharos-lib-types';
import { IMPORT_STATE } from '@klareospace/pharos-lib-types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ListImportsParams } from '../api';
import { createImportsApi, importKeys } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UseImportsOptions extends ListImportsParams {
  enabled?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function hasPendingImport(data: PaginatedResponse<ImportHistoryItem> | undefined): boolean {
  return (data?.items ?? []).some((importDoc) => importDoc.state === IMPORT_STATE.PENDING);
}

/** Refetch-interval resolver for `useQuery`'s `refetchInterval` option — polls while pending, stops once terminal. */
export const resolveRefetchInterval =
  createPendingRefetchResolver<PaginatedResponse<ImportHistoryItem>>(hasPendingImport);

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Lists the tenant's import history, scope-filtered and paginated — polls while any row on the current page is still `pending`. */
export function useImports(options?: UseImportsOptions): UseQueryResult<PaginatedResponse<ImportHistoryItem>> {
  const client = useApiClient('deliveries');
  const api = createImportsApi(client);

  const { enabled, ...filters } = options ?? {};

  return useQuery({
    enabled,
    gcTime: LIST_GC_TIME_MS,
    queryFn: (): Promise<PaginatedResponse<ImportHistoryItem>> => api.listImports(filters),
    queryKey: importKeys.list(filters),
    refetchInterval: resolveRefetchInterval,
    staleTime: LIST_STALE_TIME_MS,
  });
}
